import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Upload, Trash2, Image as ImageIcon, Video as VideoIcon, Replace, GripVertical, ArrowUp, ArrowDown, Link, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const MediaGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replacingId, setReplacingId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  
  // URL Dialog state
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlDialogType, setUrlDialogType] = useState('photo');
  const [externalUrl, setExternalUrl] = useState('');
  const [mediaName, setMediaName] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Extract Google Drive file ID from various URL formats
  const extractGoogleDriveFileId = (url) => {
    if (!url) return null;
    
    let fileId = null;
    
    // Format: drive.google.com/uc?id=FILE_ID
    if (url.includes('drive.google.com/uc')) {
      const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch) fileId = idMatch[1];
    }
    // Format: drive.google.com/thumbnail?id=FILE_ID
    if (url.includes('drive.google.com/thumbnail')) {
      const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch) fileId = idMatch[1];
    }
    // Format: drive.google.com/file/d/FILE_ID/view
    const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (fileIdMatch) fileId = fileIdMatch[1];
    
    // Format: drive.google.com/open?id=FILE_ID
    const openIdMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (!fileId && openIdMatch) fileId = openIdMatch[1];
    
    return fileId;
  };

  // Convert Google Drive URL to image URL
  const convertGoogleDriveImageUrl = (url) => {
    if (!url) return url;
    const fileId = extractGoogleDriveFileId(url);
    if (fileId) {
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
    }
    return url;
  };

  // Convert Google Drive URL to video embed URL
  const convertGoogleDriveVideoUrl = (url) => {
    if (!url) return url;
    const fileId = extractGoogleDriveFileId(url);
    if (fileId) {
      // Use Google Drive's preview/embed URL for video playback
      return `https://drive.google.com/file/d/${fileId}/preview`;
    }
    return url;
  };

  // Check if URL is a Google Drive video (needs iframe)
  const isGoogleDriveVideo = (url) => {
    return url && url.includes('drive.google.com');
  };

  // Get video thumbnail URL for Google Drive
  const getVideoThumbnailUrl = (url) => {
    if (!url) return '';
    const fileId = extractGoogleDriveFileId(url);
    if (fileId) {
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w400`;
    }
    return url;
  };

  // Helper function to get correct image URL
  const getMediaUrl = (fileUrl, isVideo = false) => {
    if (!fileUrl) return '';
    
    // Handle Google Drive URLs
    if (fileUrl.includes('drive.google.com')) {
      return isVideo ? convertGoogleDriveVideoUrl(fileUrl) : convertGoogleDriveImageUrl(fileUrl);
    }
    
    // Convert /uploads/... to /api/uploads/... for proper routing
    if (fileUrl.startsWith('/uploads/')) {
      return `${BACKEND_URL}/api${fileUrl}`;
    }
    return fileUrl.startsWith('http') ? fileUrl : `${BACKEND_URL}${fileUrl}`;
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const [photosRes, videosRes] = await Promise.all([
        axios.get(`${API}/media/list?media_type=photo`),
        axios.get(`${API}/media/list?media_type=video`),
      ]);

      if (photosRes.data.success) {
        setPhotos(photosRes.data.media);
      }
      if (videosRes.data.success) {
        setVideos(videosRes.data.media);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e, mediaType) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('media_type', mediaType);

        await axios.post(`${API}/media/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      toast.success(`${files.length} file(s) uploaded successfully!`);
      fetchMedia();
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Failed to upload media');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Add media via external URL (Google Drive, etc.)
  const handleAddExternalUrl = async () => {
    if (!externalUrl.trim()) {
      toast.error('Please enter a valid URL');
      return;
    }

    setUploading(true);

    try {
      // Use the appropriate converter based on media type
      const isVideo = urlDialogType === 'video';
      const convertedUrl = isVideo 
        ? convertGoogleDriveVideoUrl(externalUrl.trim())
        : convertGoogleDriveImageUrl(externalUrl.trim());
      
      await axios.post(`${API}/media/add-url`, {
        url: convertedUrl,
        original_url: externalUrl.trim(),
        media_type: urlDialogType,
        name: mediaName.trim() || 'External Media'
      });

      toast.success('Media added successfully!');
      setUrlDialogOpen(false);
      setExternalUrl('');
      setMediaName('');
      fetchMedia();
    } catch (error) {
      console.error('Error adding media URL:', error);
      toast.error('Failed to add media');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId, mediaType) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`${API}/media/${mediaId}`);
      toast.success('Media deleted successfully');
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    }
  };

  const handleReplace = async (e, mediaId) => {
    const file = e.target.files[0];
    if (!file) return;

    setReplacingId(mediaId);

    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.put(`${API}/media/${mediaId}/replace`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Media replaced successfully!');
      fetchMedia();
    } catch (error) {
      console.error('Error replacing media:', error);
      toast.error('Failed to replace media');
    } finally {
      setReplacingId(null);
      e.target.value = '';
    }
  };

  // Replace with external URL
  const handleReplaceWithUrl = async (mediaId, newUrl) => {
    try {
      const convertedUrl = convertGoogleDriveUrl(newUrl.trim());
      
      await axios.put(`${API}/media/${mediaId}/replace-url`, {
        url: convertedUrl,
        original_url: newUrl.trim()
      });

      toast.success('Media URL updated successfully!');
      fetchMedia();
    } catch (error) {
      console.error('Error replacing media URL:', error);
      toast.error('Failed to update media URL');
    }
  };

  const movePhoto = async (index, direction) => {
    const newPhotos = [...photos];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newPhotos.length) return;

    [newPhotos[index], newPhotos[targetIndex]] = [newPhotos[targetIndex], newPhotos[index]];
    
    const updatedPhotos = newPhotos.map((photo, idx) => ({
      ...photo,
      display_order: idx
    }));
    
    setPhotos(updatedPhotos);

    try {
      await axios.put(`${API}/media/reorder`, {
        media_orders: updatedPhotos.map((p, idx) => ({ id: p.id, display_order: idx }))
      });
      toast.success('Photo order updated');
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to update order');
      fetchMedia();
    }
  };

  const moveVideo = async (index, direction) => {
    const newVideos = [...videos];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newVideos.length) return;

    [newVideos[index], newVideos[targetIndex]] = [newVideos[targetIndex], newVideos[index]];
    
    const updatedVideos = newVideos.map((video, idx) => ({
      ...video,
      display_order: idx
    }));
    
    setVideos(updatedVideos);

    try {
      await axios.put(`${API}/media/reorder`, {
        media_orders: updatedVideos.map((v, idx) => ({ id: v.id, display_order: idx }))
      });
      toast.success('Video order updated');
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to update order');
      fetchMedia();
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newPhotos = [...photos];
    const draggedPhoto = newPhotos[draggedItem];
    
    newPhotos.splice(draggedItem, 1);
    newPhotos.splice(index, 0, draggedPhoto);
    
    setPhotos(newPhotos);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    if (draggedItem === null) return;

    const updatedPhotos = photos.map((photo, idx) => ({
      ...photo,
      display_order: idx
    }));

    try {
      await axios.put(`${API}/media/reorder`, {
        media_orders: updatedPhotos.map((p, idx) => ({ id: p.id, display_order: idx }))
      });
      toast.success('Photo order saved');
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save order');
      fetchMedia();
    }

    setDraggedItem(null);
  };

  const openUrlDialog = (type) => {
    setUrlDialogType(type);
    setExternalUrl('');
    setMediaName('');
    setUrlDialogOpen(true);
  };

  if (loading) {
    return <div className="text-center py-12">Loading media...</div>;
  }

  return (
    <div className="space-y-6" data-testid="media-gallery-container">
      <Toaster />
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Media Gallery</h1>
        <p className="text-gray-600 mt-1">Manage your photos and videos - Upload files or add Google Drive links</p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 mb-2">💡 How to add Google Drive media:</h3>
        <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
          <li>Upload your photo/video to Google Drive</li>
          <li>Right-click → Share → "Anyone with the link can view"</li>
          <li>Copy the link and click "Add from URL" below</li>
        </ol>
      </div>

      <Tabs defaultValue="photos" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="photos" data-testid="photos-tab">Photos ({photos.length})</TabsTrigger>
          <TabsTrigger value="videos" data-testid="videos-tab">Videos ({videos.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="photos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                <span>Photos</span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => openUrlDialog('photo')}
                    data-testid="add-photo-url-button"
                  >
                    <Link className="mr-2" size={16} />
                    Add from URL
                  </Button>
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <Button disabled={uploading} asChild>
                      <span data-testid="upload-photos-button">
                        <Upload className="mr-2" size={16} />
                        {uploading ? 'Uploading...' : 'Upload Files'}
                      </span>
                    </Button>
                  </label>
                  <Input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleUpload(e, 'photo')}
                    disabled={uploading}
                  />
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {photos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon className="mx-auto mb-4" size={48} />
                  <p>No photos yet</p>
                  <p className="text-sm mt-2">Upload files or add Google Drive links</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo, index) => (
                    <div
                      key={photo.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`relative group rounded-lg overflow-hidden border hover:shadow-lg transition-all ${
                        draggedItem === index ? 'opacity-50 scale-95' : ''
                      }`}
                      data-testid={`photo-item-${photo.id}`}
                    >
                      <div className="absolute top-2 left-2 z-10 bg-black/50 rounded p-1 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="text-white" size={16} />
                      </div>

                      <div className="absolute top-2 right-2 z-10 bg-green-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                        {index + 1}
                      </div>

                      {/* External URL indicator */}
                      {photo.file_url?.startsWith('http') && !photo.file_url?.includes('/api/') && (
                        <div className="absolute top-2 left-10 z-10 bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                          <Link size={10} className="inline mr-1" />
                          URL
                        </div>
                      )}

                      <img
                        src={getMediaUrl(photo.file_url)}
                        alt={photo.original_filename}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://placehold.co/400x300/1a1a1a/666666?text=Image+Error';
                        }}
                      />
                      
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center gap-2">
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => movePhoto(index, 'up')}
                            disabled={index === 0}
                            className="p-2"
                          >
                            <ArrowUp size={14} />
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => movePhoto(index, 'down')}
                            disabled={index === photos.length - 1}
                            className="p-2"
                          >
                            <ArrowDown size={14} />
                          </Button>
                        </div>

                        <label className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="secondary"
                            size="sm"
                            asChild
                            disabled={replacingId === photo.id}
                          >
                            <span>
                              {replacingId === photo.id ? '...' : <Replace size={16} />}
                            </span>
                          </Button>
                          <Input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleReplace(e, photo.id)}
                            disabled={replacingId === photo.id}
                          />
                        </label>

                        <Button
                          variant="destructive"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDelete(photo.id, 'photo')}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      
                      <div className="p-2 text-xs text-gray-600 truncate">
                        {photo.original_filename}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="videos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                <span>Videos</span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => openUrlDialog('video')}
                    data-testid="add-video-url-button"
                  >
                    <Link className="mr-2" size={16} />
                    Add from URL
                  </Button>
                  <label htmlFor="video-upload" className="cursor-pointer">
                    <Button disabled={uploading} asChild>
                      <span data-testid="upload-videos-button">
                        <Upload className="mr-2" size={16} />
                        {uploading ? 'Uploading...' : 'Upload Files'}
                      </span>
                    </Button>
                  </label>
                  <Input
                    id="video-upload"
                    type="file"
                    accept="video/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleUpload(e, 'video')}
                    disabled={uploading}
                  />
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {videos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <VideoIcon className="mx-auto mb-4" size={48} />
                  <p>No videos yet</p>
                  <p className="text-sm mt-2">Upload files or add Google Drive links</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {videos.map((video, index) => (
                    <div
                      key={video.id}
                      className="relative group rounded-lg overflow-hidden border hover:shadow-lg transition-shadow"
                      data-testid={`video-item-${video.id}`}
                    >
                      <div className="absolute top-2 left-2 z-10 bg-green-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                        {index + 1}
                      </div>

                      {/* External URL indicator */}
                      {video.file_url?.startsWith('http') && !video.file_url?.includes('/api/') && (
                        <div className="absolute top-2 left-10 z-10 bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                          <Link size={10} className="inline mr-1" />
                          URL
                        </div>
                      )}
                      
                      {/* Use iframe for Google Drive videos, video tag for local files */}
                      {isGoogleDriveVideo(video.file_url) ? (
                        <iframe
                          src={getMediaUrl(video.file_url, true)}
                          className="w-full h-48"
                          allow="autoplay; encrypted-media"
                          allowFullScreen
                        />
                      ) : (
                        <video
                          src={getMediaUrl(video.file_url, true)}
                          className="w-full h-48 object-cover"
                          controls
                        />
                      )}
                      
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center gap-2 pointer-events-none group-hover:pointer-events-auto">
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => moveVideo(index, 'up')}
                            disabled={index === 0}
                            className="p-2"
                          >
                            <ArrowUp size={14} />
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => moveVideo(index, 'down')}
                            disabled={index === videos.length - 1}
                            className="p-2"
                          >
                            <ArrowDown size={14} />
                          </Button>
                        </div>

                        <label className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="secondary"
                            size="sm"
                            asChild
                            disabled={replacingId === video.id}
                          >
                            <span>
                              {replacingId === video.id ? '...' : <Replace size={16} />}
                            </span>
                          </Button>
                          <Input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={(e) => handleReplace(e, video.id)}
                            disabled={replacingId === video.id}
                          />
                        </label>

                        <Button
                          variant="destructive"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDelete(video.id, 'video')}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      
                      <div className="p-2 text-xs text-gray-600 truncate">
                        {video.original_filename}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add URL Dialog */}
      <Dialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add {urlDialogType === 'photo' ? 'Photo' : 'Video'} from URL
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Google Drive or External URL</label>
              <Input
                placeholder="https://drive.google.com/file/d/..."
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                className="mt-1"
                data-testid="external-url-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Paste your Google Drive share link here
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Name (optional)</label>
              <Input
                placeholder="My Photo"
                value={mediaName}
                onChange={(e) => setMediaName(e.target.value)}
                className="mt-1"
                data-testid="media-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUrlDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddExternalUrl} disabled={uploading || !externalUrl.trim()}>
              {uploading ? 'Adding...' : 'Add Media'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaGallery;
